import React, {
  useMemo,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import api from 'api';
import { Lead, LeadStages, LeadState } from 'models/Lead';
import LeadsContext, { ReleaseFileUndoInstance } from './Leads.context';
import ListingContext from '../Listing/Listing.context';
import {
  AUTOMATIC_REORDER,
  getGridLeadsEmptyState,
  getGridLeadsNumber,
  getNumberOfUnreadMessagesNotes,
  getNumberOfUnrepliedMessagesNotes,
  isLeadStage,
  isWaitlisted,
  LEAD_STATE,
  moveLead,
  removeLeadFromArray,
  sortLeads,
  STATE_REORDERING,
  updateLeadInArray,
  updateLeadsInArray,
  WAITLISTED_KEYWORD,
} from '../../utils';
import { LeadsReorderingProps } from 'api/portfolio/seller';
import useCallbackRef from 'hooks/useCallbackRef';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { LeadsStateChangedEventData } from 'services/socketService/socketEventData';
import NotPaidDeal from 'components/NotPaidDeal';
import { LEAD_DRAWER_OPTIONS, LeadDrawerOptions } from '../../../../models';
import useQueryParams from 'hooks/useQueryParams';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { SORTING_VALUES } from '../../components/LeadsTableContainer/data';
import { TotalChatCount } from 'models/DealRoom';
import WidthrawLeadModal from '../../components/WidthrawLeadModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useRefetch from 'hooks/useRefetch';
import DealContext from '../Deal';
import { useTranslation } from 'react-i18next';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import usePageParams from 'hooks/usePageParams';
import useUndoActions from 'hooks/useUndoActions';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { RELEASE_FILE_UNDO_COPY } from '../../components/LeadsTableContainer/components/DisplayFilesByLead/data';
import { getHasElementsInArray } from 'utils';
import ReorderFilesDrawer from '../../components/LeadsTableContainer/components/ReorderFilesDrawer';
import DiscoveringFeature from 'components/DiscoveringFeature';
import {
  FILE_ACCESS_SETTINGS_BUTTON_ID,
  FIRST_RELEASE_DISCOVERING_FEATURE,
} from 'data/discoveringFeature';
import useMatchMedia from 'hooks/useMatchMedia';
import { ManageLeadDrawer } from '../../components/ManageLeadDrawer';

type SellerProps = LeadsReorderingProps;

const DEFAULT_SORT = `-${SORTING_VALUES.lastSeen}`;
const RELATIONS = '["buyer"]';

export const ONGOING_PAGE = 'ongoingPage';
export const INACTIVE_PAGE = 'inactivePage';

const LeadsProvider: React.FC = ({ children }) => {
  const { popServerError } = useContext(PopNotificationsContext);
  const {
    currentUser: { seenFileReorderFeature },
    isSuperAdmin,
    updatePreferences,
  } = useContext(CurrentUserContext);
  const {
    enquiry,
    workflowConfigurationStepsCapacity,
    dealRoom,
    listing,
    isTableView,
    areActionsDisabled,
    hasUnlockPremiumFeatures,
  } = useContext(ListingContext);
  const { dealRoomTopNodes, dealRoomStarredNodes, setDealRoom } =
    useContext(DealContext);
  const {
    params: { sort, leadId },
    setQueryParam,
  } = useQueryParams();
  const { _id: enquiryId, leadIds } = enquiry || {};

  const ongoingPage = usePageParams(ONGOING_PAGE);
  const inactivePage = usePageParams(INACTIVE_PAGE);

  const { limit } = useNumberOfResultsPerPage();

  const { t } = useTranslation();

  const isAboveLaptop = useMatchMedia('isAboveLaptop');

  const [totalChatCount, setTotalChatCount] = useState<TotalChatCount>();
  const [withdrawModal, withdrawModalRef] = useCallbackRef<ModalRef>();
  const [withdrawnLead, setWithdrawnLead] = useState(null);
  const [showInactiveLeads, setShowInactiveLeads] = useState(false);

  const [releaseFileUndo, setReleaseFileUndo] =
    useState<ReleaseFileUndoInstance>(null);

  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedLeadDrawer, setSelectedLeadDrawer] =
    useState<LeadDrawerOptions | null>(null);

  const [manageLeadDrawer, manageLeadDrawerRef] =
    useCallbackRef<SideDrawerRef>();

  const [reorderFilesDrawer, reorderFilesDrawerRef] =
    useCallbackRef<SideDrawerRef>();

  // table states

  // ongoing
  const [isLoadingOngoingTable, setIsLoadingOngoingTable] = useState(true);
  const [totalOngoingPages, setTotalOngoingPages] = useState(1);
  const [onGoingTableLeads, setOngoingTableLeads] = useState<Lead[]>([]);
  // inactive
  const [isLoadingWaitlistedTable, setIsLoadingWaitlistedTable] =
    useState(true);
  const [totalWaitlistedPages, setTotalWaitlistedPages] = useState(1);
  const [waitlistedTableLeads, setWaitlistedTableLeads] = useState<Lead[]>([]);

  const { refetchFlag, refetch } = useRefetch();

  // grid states

  const [isLoadingGrid, setIsLoadingGrid] = useState(true);
  const [updatingLeads, setIsUpdatingLeads] = useState([]);
  const [gridLeads, setGridLeads] = useState<Record<LeadState, Lead[]>>(
    getGridLeadsEmptyState(),
  );
  const [scrollableContainer, scrollableContainerRef] = useCallbackRef(null);
  const [lastSuccessfulLeads, setLastSuccessfulLeads] = useState<
    Record<LeadState, Lead[]>
  >(getGridLeadsEmptyState());
  const [gridSort, setGridSort] = useState<Record<LeadState, string>>(null);

  const { totalUnreadChatsCount, totalUnrepliedChatsCount } =
    totalChatCount || {};

  const getMixpanelProps = useGetMixpanelProps();

  const tableHasAnyLeads = useMemo(
    () => !!onGoingTableLeads?.length || !!waitlistedTableLeads?.length,
    [onGoingTableLeads?.length, waitlistedTableLeads?.length],
  );

  const gridHasAnyLeads = useMemo(
    () => Object.values(gridLeads).some((leadsArray) => leadsArray.length),
    [gridLeads],
  );

  const hasAnyLeads = useMemo(
    () => (isTableView ? tableHasAnyLeads : gridHasAnyLeads),
    [isTableView, tableHasAnyLeads, gridHasAnyLeads],
  );

  const showFileAccessSettings = useMemo(
    () =>
      !!(
        (getHasElementsInArray(dealRoomStarredNodes) ||
          getHasElementsInArray(dealRoomTopNodes)) &&
        onGoingTableLeads[0]?.canManageDataRoom
      ),
    [dealRoomStarredNodes, dealRoomTopNodes, onGoingTableLeads],
  );

  const showDiscoveryFeature = useMemo(
    () =>
      (isTableView || !isAboveLaptop) &&
      showFileAccessSettings &&
      !seenFileReorderFeature,
    [
      isAboveLaptop,
      isTableView,
      seenFileReorderFeature,
      showFileAccessSettings,
    ],
  );

  const closeDiscoveringFeature = useCallback(() => {
    if (seenFileReorderFeature) return;

    updatePreferences({ seenFileReorderFeature: true });
  }, [seenFileReorderFeature, updatePreferences]);

  const openReorderFilesDrawer = useCallback(() => {
    reorderFilesDrawer?.setOpen(true);
    closeDiscoveringFeature();
  }, [closeDiscoveringFeature, reorderFilesDrawer]);

  const { waitlistedLeads, ongoingLeads } = useMemo(
    () =>
      Object.entries(gridLeads).reduce<
        Record<'waitlistedLeads' | 'ongoingLeads', Record<string, Lead[]>>
      >(
        (acc, [key, value]) => {
          const prop = key.includes(WAITLISTED_KEYWORD)
            ? 'waitlistedLeads'
            : 'ongoingLeads';

          const sort =
            gridSort?.[key.replace(WAITLISTED_KEYWORD, '') as LeadState];
          const sortedLeads = sort ? sortLeads(value, sort) : value;

          return {
            ...acc,
            [prop]: { ...acc[prop], [key]: sortedLeads },
          };
        },
        { waitlistedLeads: {}, ongoingLeads: {} },
      ),
    [gridLeads, gridSort],
  );

  const numberOfOnGoingGridLeads = useMemo(
    () => getGridLeadsNumber(ongoingLeads),
    [ongoingLeads],
  );

  const numberOfWaitlistedGridLeads = useMemo(
    () => getGridLeadsNumber(waitlistedLeads),
    [waitlistedLeads],
  );

  const numberOfWaitlistedGridUnreadMessagesNotes = useMemo(
    () => getNumberOfUnreadMessagesNotes(waitlistedLeads),
    [waitlistedLeads],
  );

  const numberOfWaitlistedGridUnrepliedMessagesNotes = useMemo(
    () => getNumberOfUnrepliedMessagesNotes(waitlistedLeads),
    [waitlistedLeads],
  );

  const onGridSortStep = useCallback(
    (key: LeadStages, sort: string) =>
      setGridSort((old) => ({ ...old, [key]: sort })),
    [],
  );

  const openWithdrawModal = useCallback(
    (lead: Lead) => {
      setWithdrawnLead(lead);
      withdrawModal?.open();
    },
    [withdrawModal],
  );

  const updateLeadsHandle = useCallback(
    ({ updatedListingEnquiry, updatedLeads }: LeadsStateChangedEventData) => {
      if (enquiryId !== updatedListingEnquiry._id) return;

      setGridLeads(updatedLeads);
      refetch();
    },
    [enquiryId, refetch],
  );

  useSocketEvent(LISTEN_EVENTS.LEADS_STATE_CHANGED, updateLeadsHandle);

  useEffect(() => {
    if (!sort) {
      setQueryParam('sort', DEFAULT_SORT);
    }
  }, [sort, setQueryParam]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.dealRoom.getTotalChatCount(dealRoom?._id);

        setTotalChatCount(data);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      }
    })();
  }, [dealRoom?._id, refetchFlag, getMixpanelProps]);

  // grid view
  useEffect(() => {
    (async () => {
      setIsLoadingGrid(true);
      try {
        const {
          data: { leads },
        } = await api.portfolio.seller.getLeadsForListing(enquiryId, {
          $relations: RELATIONS,
        });

        setGridLeads(leads);
        setLastSuccessfulLeads(leads);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoadingGrid(false);
      }
    })();
  }, [enquiryId, leadIds?.length, popServerError]);

  // ongoing table view
  useEffect(() => {
    if (!sort) return;

    (async () => {
      setIsLoadingOngoingTable(true);
      try {
        const {
          data: { items, totalPages },
        } = await api.portfolio.seller.getTableLeads(enquiryId, {
          _id: leadId,
          $page: ongoingPage,
          $sort: sort,
          $limit: limit,
          $relations: RELATIONS,
          $and: '[{"withdrawnInterest":false},{"isInactive":false},{"isPrivateViewer":false},{"isRequestedPrivateViewer":false}]',
        });

        setTotalOngoingPages(totalPages);
        setOngoingTableLeads(items);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoadingOngoingTable(false);
      }
    })();
  }, [
    enquiryId,
    leadIds?.length,
    refetchFlag,
    ongoingPage,
    sort,
    leadId,
    limit,
    popServerError,
  ]);

  // inactive table
  useEffect(() => {
    (async () => {
      setIsLoadingWaitlistedTable(true);
      try {
        const {
          data: { items, totalPages },
        } = await api.portfolio.seller.getTableLeads(enquiryId, {
          _id: leadId,
          $page: inactivePage,
          $limit: limit,
          $relations: RELATIONS,
          $or: '[{"withdrawnInterest":true},{"isInactive":true},{"isPrivateViewer":true},{"isRequestedPrivateViewer":true}]',
        });

        setTotalWaitlistedPages(totalPages);
        setWaitlistedTableLeads(items);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoadingWaitlistedTable(false);
      }
    })();
  }, [
    enquiryId,
    leadIds?.length,
    refetchFlag,
    inactivePage,
    leadId,
    limit,
    popServerError,
  ]);

  const openLeadDrawer = useCallback(
    (lead: Lead, tab: LeadDrawerOptions = LEAD_DRAWER_OPTIONS.PROFILE) => {
      setSelectedLead(lead);
      setSelectedLeadDrawer(tab);
      manageLeadDrawer.setOpen(true);
    },
    [manageLeadDrawer],
  );

  const closeLeadDrawer = useCallback(() => {
    setSelectedLead(null);
    setSelectedLeadDrawer(null);
    manageLeadDrawer.setOpen(false);
  }, [manageLeadDrawer]);

  const removeLead = useCallback((leadId: string) => {
    setOngoingTableLeads((old) => removeLeadFromArray(old, leadId));
    setWaitlistedTableLeads((old) => removeLeadFromArray(old, leadId));
    setGridLeads((old) =>
      Object.values(LEAD_STATE).reduce(
        (acc, stage) => ({
          ...acc,
          [stage]: removeLeadFromArray(old[stage], leadId),
        }),
        {} as Record<LeadState, Lead[]>,
      ),
    );
  }, []);

  const updateTableLeads = useCallback(
    (newLead: Lead, isChangingActivity?: boolean) => {
      if (newLead.withdrawnInterest) {
        isChangingActivity
          ? moveLead(newLead, setOngoingTableLeads, setWaitlistedTableLeads)
          : setWaitlistedTableLeads((old) => updateLeadInArray(old, newLead));
      } else {
        isChangingActivity
          ? moveLead(newLead, setWaitlistedTableLeads, setOngoingTableLeads)
          : setOngoingTableLeads((old) => updateLeadInArray(old, newLead));
      }
    },
    [],
  );

  const updateLeadWithoutSelected = useCallback(
    (newLead: Lead, isChangingActivity?: boolean) => {
      updateTableLeads(newLead, isChangingActivity);
      setGridLeads((old) => {
        const updatedStages = Object.values(LEAD_STATE).reduce(
          (acc, stage) => ({
            ...acc,
            [stage]: updateLeadInArray(old[stage], newLead),
          }),
          {} as Record<LeadState, Lead[]>,
        );
        return updatedStages;
      });
    },
    [updateTableLeads],
  );

  const updateLead = useCallback(
    (newLead: Lead, isChangingActivity?: boolean) => {
      updateLeadWithoutSelected(newLead, isChangingActivity);

      if (selectedLead?._id === newLead._id) setSelectedLead(newLead);
    },
    [selectedLead?._id, updateLeadWithoutSelected],
  );

  const refetchLead = useCallback(
    async (leadId: string) => {
      if (!leadId) return;

      try {
        const { data } = await api.portfolio.seller.getLead(leadId);
        updateLeadWithoutSelected(data);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      }
    },
    [getMixpanelProps, updateLeadWithoutSelected],
  );

  const {
    isUndoAvailable: isReleaseFileUndoAvailable,
    setUndoTimeout: setReleaseFileUndoTimeout,
    clearUndoTimeout: clearReleaseFileUndoTimeout,
  } = useUndoActions();

  const updateReleaseFile = useCallback(
    (dataRoomNodeId: string) =>
      setReleaseFileUndo((prev) =>
        prev?.dataRoomNodeId === dataRoomNodeId ? null : prev,
      ),
    [],
  );

  const showReleaseFileUndo = useCallback(
    (file: ReleaseFileUndoInstance) => {
      clearReleaseFileUndoTimeout();
      setReleaseFileUndo(file);
      setReleaseFileUndoTimeout(() => updateReleaseFile(file.dataRoomNodeId));
    },
    [clearReleaseFileUndoTimeout, setReleaseFileUndoTimeout, updateReleaseFile],
  );

  const [onUndoReleaseFile, isUndoingReleaseFile] = useCallbackAsync(
    async () => {
      if (!releaseFileUndo) return;

      const { leadId, dataRoomNodeId } = releaseFileUndo;

      clearReleaseFileUndoTimeout();

      const { data } = await api.portfolio.seller.undoReleaseFileForLead(
        leadId,
        dataRoomNodeId,
      );

      updateLead(data);
      updateReleaseFile(dataRoomNodeId);
    },
  );

  const isUndoingReleaseFileForNode = useCallback(
    (dataRoomNodeId: string) =>
      isUndoingReleaseFile &&
      releaseFileUndo?.dataRoomNodeId === dataRoomNodeId,
    [isUndoingReleaseFile, releaseFileUndo?.dataRoomNodeId],
  );

  const updateLeads = useCallback(
    (leads: Lead[]) => {
      setOngoingTableLeads((old) => updateLeadsInArray(old, leads));
      setWaitlistedTableLeads((old) => updateLeadsInArray(old, leads));
      setGridLeads((old) => {
        const updatedStages = Object.values(LEAD_STATE).reduce(
          (acc, stage) => ({
            ...acc,
            [stage]: updateLeadsInArray(old[stage], leads),
          }),
          {} as Record<LeadState, Lead[]>,
        );
        return updatedStages;
      });

      const newSelectedLead = leads?.find(
        ({ _id }) => _id === selectedLead?._id,
      );

      if (newSelectedLead) {
        setSelectedLead(newSelectedLead);
      }
    },
    [selectedLead],
  );

  const addUpdatingLead = useCallback(
    (leadId: string) => setIsUpdatingLeads((old) => [...old, leadId]),
    [],
  );

  const removeUpdatingLead = useCallback(
    (leadId: string) =>
      setIsUpdatingLeads((old) => old.filter((id) => id !== leadId)),
    [],
  );

  const checkIsLeadUpdating = useCallback(
    (leadId: string) => !!updatingLeads.find((id) => id === leadId),
    [updatingLeads],
  );

  const changeState = useCallback(
    async (
      lead: Lead,
      newStateProps: LeadState,
      newIndex: number,
      recursiveCall = false,
      revokeDataRoomAccess?: boolean,
    ) => {
      const newIndexInc = newIndex + 1;
      let newState = newStateProps;
      let isManualWaitlisted = true;
      const isChangingState = newState !== lead.leadState;
      let wasFullState = false;

      if (
        workflowConfigurationStepsCapacity[newState] &&
        workflowConfigurationStepsCapacity[newState] <
          gridLeads[newState].length + 1 &&
        !recursiveCall &&
        isChangingState
      ) {
        newState = STATE_REORDERING[newStateProps as LeadStages];
        isManualWaitlisted = false;
      }

      if (
        (newState === lead.leadState && lead.index === newIndexInc) ||
        (newState === lead.leadState && !isManualWaitlisted) ||
        !newState
      )
        return;

      const shouldDecrementIndex = !isChangingState && lead.index < newIndexInc;
      const newIndexVal = shouldDecrementIndex ? newIndexInc - 1 : newIndexInc;
      let newLeads: Record<LeadState, Lead[]>;

      setGridSort(null);

      setGridLeads((old) => {
        const newStateArray = !isChangingState
          ? old[newState]
              .filter((oldEl) => oldEl._id !== lead._id)
              .map((element) =>
                lead.index < newIndexInc
                  ? element.index > lead.index && element.index <= newIndexVal
                    ? {
                        ...element,
                        index: element.index - 1,
                      }
                    : element
                  : element.index < lead.index && element.index >= newIndexInc
                  ? {
                      ...element,
                      index: element.index + 1,
                    }
                  : element,
              )
          : old[newState].map((element) =>
              element.index < newIndexInc
                ? element
                : { ...element, index: element.index + 1 },
            );

        newStateArray.splice(newIndexVal - 1, 0, {
          ...lead,
          leadState: newState,
          changeLeadStateDate: new Date(),
          index: newIndexVal,
          isManualWaitlisted,
        });

        wasFullState =
          old[lead.leadState].length ===
          workflowConfigurationStepsCapacity[lead.leadState];

        const oldStateArray = old[lead.leadState]
          .filter((oldEl) => oldEl._id !== lead._id)
          .map((element) =>
            element.index < lead.index
              ? element
              : { ...element, index: element.index - 1 },
          );

        newLeads = {
          ...old,
          [lead.leadState]: oldStateArray,
          [newState]: newStateArray,
        };

        return newLeads;
      });

      const route = isChangingState
        ? isLeadStage(newState)
          ? isWaitlisted(lead.leadState)
            ? 'removeFromWaitlist'
            : 'changeBuyersState'
          : 'addToWaitlist'
        : 'changeBuyersIndex';

      try {
        addUpdatingLead(lead._id);

        const { data: updatedLead } = await api.portfolio.seller[route](
          isChangingState ? enquiry?._id : lead._id,
          {
            leadId: lead._id,
            oldLeadState: lead.leadState,
            newLeadState: newState,
            oldIndex: lead.index,
            newIndex: newIndexVal,
            isManualWaitlisted,
            revokeDataRoomAccess,
          } as SellerProps,
        );

        const isChangingActivity =
          lead.withdrawnInterest !== updatedLead.withdrawnInterest;

        setLastSuccessfulLeads(newLeads);
        updateLead(updatedLead, isChangingActivity);

        if (
          isLeadStage(lead.leadState) &&
          wasFullState &&
          isChangingState &&
          newLeads
        ) {
          const automaticalyReordered = newLeads[
            STATE_REORDERING[lead.leadState]
          ].find((waitlisted) => !waitlisted.isManualWaitlisted);

          if (automaticalyReordered) {
            changeState(
              automaticalyReordered,
              AUTOMATIC_REORDER[
                automaticalyReordered.leadState as keyof typeof AUTOMATIC_REORDER
              ],
              newLeads[
                AUTOMATIC_REORDER[
                  automaticalyReordered.leadState as keyof typeof AUTOMATIC_REORDER
                ]
              ]?.length || 0,
              true,
            );
          }
        }
      } catch (e) {
        popServerError(e);
        setGridLeads(lastSuccessfulLeads);
      } finally {
        removeUpdatingLead(lead._id);
      }
    },
    [
      enquiry?._id,
      gridLeads,
      lastSuccessfulLeads,
      workflowConfigurationStepsCapacity,
      addUpdatingLead,
      popServerError,
      removeUpdatingLead,
      updateLead,
    ],
  );

  if (!hasUnlockPremiumFeatures && !isSuperAdmin) {
    return (
      <NotPaidDeal
        dealRoomId={dealRoom._id}
        listingId={listing?._id}
        listingType={listing?.type}
        isDisabled={areActionsDisabled}
      />
    );
  }

  return (
    <LeadsContext.Provider
      value={{
        numberOfOnGoingGridLeads,
        numberOfWaitlistedGridLeads,
        numberOfWaitlistedGridUnreadMessagesNotes,
        numberOfWaitlistedGridUnrepliedMessagesNotes,
        hasAnyLeads,
        isLoadingGrid,
        waitlistedLeads,
        ongoingLeads,
        gridLeads,
        gridHasAnyLeads,
        scrollableContainer,
        totalOngoingPages,
        onGoingTableLeads,
        totalWaitlistedPages,
        waitlistedTableLeads,
        isLoadingWaitlistedTable,
        isLoadingOngoingTable,
        tableHasAnyLeads,
        selectedLead,
        selectedLeadDrawer,
        gridSort,
        showInactiveLeads,
        showFileAccessSettings,
        totalUnreadChatsCount,
        totalUnrepliedChatsCount,
        isLoadingTable: isLoadingOngoingTable || isLoadingWaitlistedTable,
        refetchLead,
        setShowInactiveLeads,
        closeDiscoveringFeature,
        onGridSortStep,
        updateLead,
        updateLeads,
        removeLead,
        refetch,
        changeState,
        scrollableContainerRef,
        setSelectedLead,
        setSelectedLeadDrawer,
        checkIsLeadUpdating,
        openLeadDrawer,
        closeLeadDrawer,
        openWithdrawModal,
        showReleaseFileUndo,
        isUndoingReleaseFileForNode,
        openReorderFilesDrawer,
      }}
    >
      {children}

      <ManageLeadDrawer ref={manageLeadDrawerRef} />

      {showDiscoveryFeature && (
        <DiscoveringFeature
          selector={`#${FILE_ACCESS_SETTINGS_BUTTON_ID}`}
          showBannerNotification
          createdAt={FIRST_RELEASE_DISCOVERING_FEATURE}
          title={t('configureFileAccess')}
          description={t('configureFileAccessDescription')}
          onClose={closeDiscoveringFeature}
        />
      )}

      <ReorderFilesDrawer
        ref={reorderFilesDrawerRef}
        dealRoom={dealRoom}
        dealRoomTopNodes={dealRoomTopNodes}
        dealRoomStarredNodes={dealRoomStarredNodes}
        onClose={(dataRoomTopNodes, dataRoomStarredNodes) =>
          setDealRoom((prev) => ({
            ...prev,
            dataRoomTopNodes,
            dataRoomStarredNodes,
          }))
        }
      />

      <WidthrawLeadModal ref={withdrawModalRef} lead={withdrawnLead} />

      <PopNotificationWithUndo
        isUndoAvailable={isReleaseFileUndoAvailable}
        disabled={isUndoingReleaseFile}
        onUndo={onUndoReleaseFile}
      >
        <p>
          {releaseFileUndo?.accessLevel &&
            t(RELEASE_FILE_UNDO_COPY[releaseFileUndo.accessLevel], {
              documentName: releaseFileUndo.name,
            })}
        </p>
      </PopNotificationWithUndo>
    </LeadsContext.Provider>
  );
};
export default LeadsProvider;
